import React, { useEffect, useMemo, useState } from 'react';
import { SwitchDeviceCard } from '@mindly/ui-components';
import { StyledVideoCallSwitchDeviceModal } from '../VideoCall.style';
import { JSX } from '@ionic/core/components';
import { ICameraVideoTrack } from 'agora-rtc-sdk-ng';
import { useIntl } from 'react-intl';

type SwitchDeviceModalPropsType = {
  deviceList?: MediaDeviceInfo[];
  videoTrack?: ICameraVideoTrack;
  onClickVideoDeviceHandler?: (deviceID: string) => Promise<void>;
  onDisableVideoDeviceHandler?: () => void;
  deviceType: 'video' | 'audio';
} & JSX.IonModal;

const SwitchDeviceModal: React.FC<SwitchDeviceModalPropsType> = ({
  deviceList,
  videoTrack,
  deviceType,
  onClickVideoDeviceHandler,
  onDisableVideoDeviceHandler,
  ...props
}) => {
  const [currentVideoDeviceID, setCurrentVideoDeviceID] = useState<string | null>();

  const intl = useIntl();

  const filteredDeviceList = useMemo(() => {
    const filteredVideoDevices = deviceList?.filter((el) => el.kind === 'videoinput');
    const filteredAudioDevices = deviceList?.filter((el) => el.kind === 'audioinput');

    if (deviceType === 'video') return filteredVideoDevices;
    if (deviceType === 'audio') return filteredAudioDevices;
  }, [deviceList?.length, deviceType]);

  const activeDeviceID = useMemo(() => {
    const activeVideoDeviceID = videoTrack?.enabled
      ? videoTrack?.getMediaStreamTrack().getSettings().deviceId
      : undefined;

    if (deviceType === 'video') return activeVideoDeviceID;
  }, [videoTrack?.enabled, currentVideoDeviceID, deviceType]);

  const onClickDeviceHandler = useMemo(() => {
    if (deviceType === 'video') return onClickVideoDeviceHandler;
  }, [deviceType, onClickVideoDeviceHandler]);

  const disableDeviceHandler = useMemo(() => {
    if (deviceType === 'video') return onDisableVideoDeviceHandler;
  }, [deviceType, onDisableVideoDeviceHandler]);

  const heading = useMemo(() => {
    if (deviceType === 'video')
      return intl.formatMessage({
        id: 'general.cam',
        defaultMessage: 'Камера',
      });
    if (deviceType === 'audio')
      return intl.formatMessage({
        id: 'general.mic',
        defaultMessage: 'Мікрофон',
      });
  }, [deviceType, intl]);

  const isDisabled = useMemo(() => {
    if (deviceType === 'video') return !videoTrack?.enabled;
  }, [deviceType, videoTrack?.enabled]);

  useEffect(() => {
    setCurrentVideoDeviceID(videoTrack?.getMediaStreamTrack().getSettings().deviceId);
  }, [videoTrack?.getMediaStreamTrack().getSettings().deviceId]);

  useEffect(() => {}, [deviceList?.length]);

  return (
    <StyledVideoCallSwitchDeviceModal {...props} mode={'md'}>
      <SwitchDeviceCard
        heading={heading}
        deviceList={filteredDeviceList}
        activeDeviceID={activeDeviceID}
        onClickDeviceHandler={onClickDeviceHandler}
        deviceType={deviceType}
        disableDeviceHandler={disableDeviceHandler}
        isDisabled={isDisabled}
        translations={{
          micro: intl.formatMessage({
            id: 'general.mic',
            defaultMessage: 'Мікрофон',
          }),
          camera: intl.formatMessage({
            id: 'general.cam',
            defaultMessage: 'Камера',
          }),
          offText: intl.formatMessage({
            id: 'general.off',
            defaultMessage: 'Вимкнути',
          }),
        }}
      />
    </StyledVideoCallSwitchDeviceModal>
  );
};

export default React.memo(SwitchDeviceModal);
